import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getNotifications, markNotificationRead } from "../api/client";

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const [notifications, setNotifications] = useState([]); // unread only
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const unreadCount = notifications.length;

  const addNotification = useCallback((notif) => {
    if (!notif || typeof notif.id === "undefined") return;
    setNotifications((prev) => (prev.some((n) => n.id === notif.id) ? prev : [notif, ...prev]));
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      setLoading(true);
      const list = await getNotifications();
      // API already returns unread sorted desc; precompute display time once
      const enhanced = (list || []).map((n) => ({
        ...n,
        _displayTime: n?.created_at ? new Date(n.created_at).toLocaleString() : "",
      }));
      setNotifications(enhanced);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, []);

  const markAsRead = useCallback(async (notificationUserId) => {
    try {
      await markNotificationRead(notificationUserId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationUserId));
    } catch (err) {
      console.error("Failed to mark notification as read", err);
      throw err;
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const ids = notifications.map((n) => n.id);
    if (ids.length === 0) return;
    // Batch API calls but update state once
    await Promise.all(ids.map((id) => markNotificationRead(id)));
    const idSet = new Set(ids);
    setNotifications((prev) => prev.filter((n) => !idSet.has(n.id)));
  }, [notifications]);

  useEffect(() => {
    fetchUnread();
  }, [fetchUnread]);

  useEffect(() => {
    const handler = () => {
      // Fetch canonical unread list to ensure correct IDs from API
      fetchUnread();
    };
    window.addEventListener("app:new-notification", handler);
    return () => window.removeEventListener("app:new-notification", handler);
  }, [fetchUnread]);

  useEffect(() => {
    const onFocus = () => fetchUnread();
    const onOnline = () => fetchUnread();
    const onWsConnected = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    window.addEventListener("app:ws-connected", onWsConnected);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("app:ws-connected", onWsConnected);
    };
  }, [fetchUnread]);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    loaded,
    addNotification,
    fetchUnread,
    markAsRead,
    markAllAsRead,
  }), [notifications, unreadCount, loading, loaded, addNotification, fetchUnread, markAsRead, markAllAsRead]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationsProvider");
  return ctx;
}
